import { useCallback, useEffect, useRef } from 'react';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { ReadContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import type {
	InsertedContentIdentity,
	RemixResultTracking,
	RegisterRemixResultTracking,
} from './types';
import { getMauiIdFromAdfNode } from './utils/remix-ids';

const ACTIONED_EVENT_DELAY = 30000;

const isNonEmptyString = (value: unknown): value is string =>
	typeof value === 'string' && value.length > 0;

const adfContainsIdentity = (value: unknown, identity: InsertedContentIdentity): boolean => {
	if (Array.isArray(value)) {
		return value.some((item) => adfContainsIdentity(item, identity));
	}
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const node = value as { attrs?: Record<string, unknown>; content?: unknown };
	const identityMatches =
		identity.attribute === 'mauiVersion'
			? node.attrs?.localId === identity.localId && getMauiIdFromAdfNode(node) === identity.value
			: node.attrs?.[identity.attribute] === identity.value;
	if (identityMatches) {
		return true;
	}

	return adfContainsIdentity(node.content, identity);
};

export const useRemixRetentionTracker = (
	trackAppliedResult?: (options: RemixResultTracking) => void,
): RegisterRemixResultTracking => {
	const { trackAIMAUEvent, sendAnalyticsEvent } = useAnalytics();
	const trackAppliedResultRef = useRef(trackAppliedResult);
	trackAppliedResultRef.current = trackAppliedResult;
	const sendAnalyticsEventRef = useRef(sendAnalyticsEvent);
	sendAnalyticsEventRef.current = sendAnalyticsEvent;
	const trackAIMAUEventRef = useRef(trackAIMAUEvent);
	trackAIMAUEventRef.current = trackAIMAUEvent;
	const isActiveRef = useRef(true);
	const pendingKeysRef = useRef(new Set<string>());
	const completedKeysRef = useRef(new Set<string>());
	const timeoutIdsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

	useEffect(() => {
		const timeoutIds = timeoutIdsRef.current;
		const pendingKeys = pendingKeysRef.current;
		const completedKeys = completedKeysRef.current;
		isActiveRef.current = true;

		return () => {
			isActiveRef.current = false;
			timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
			timeoutIds.clear();
			pendingKeys.clear();
			completedKeys.clear();
		};
	}, []);

	return useCallback<RegisterRemixResultTracking>(
		({
			adoptedAnalyticsEvent,
			analyticsEvent,
			shouldDelayActionedEvent = true,
			operation,
			previousResultId,
			sourceResultId,
			identity,
			insertedAt = Date.now(),
			interactionSource,
			receiverId,
		}) => {
			const eventAttributes = analyticsEvent.attributes as Record<string, unknown> | undefined;
			const dynamicUiType = eventAttributes?.dynamicUiType;
			if (
				isActiveRef.current &&
				trackAppliedResultRef.current &&
				isNonEmptyString(analyticsEvent.singleInstrumentationID) &&
				isNonEmptyString(dynamicUiType) &&
				isExperimentEnabled('platform_maui_retention_analytics')
			) {
				trackAppliedResultRef.current({
					...(identity.attribute === 'id'
						? { mediaId: identity.value }
						: {
								localId: identity.attribute === 'mauiVersion' ? identity.localId : identity.value,
							}),
					resultId: identity.attribute === 'mauiVersion' ? identity.value : undefined,
					previousResultId,
					attributes: {
						pageId: receiverId,
						singleInstrumentationID: analyticsEvent.singleInstrumentationID,
						operation: operation ?? (eventAttributes?.userAction === 'edit' ? 'edit' : 'create'),
						dynamicUiType,
						dynamicUiSubtype: eventAttributes?.dynamicUiSubtype,
						...(sourceResultId ? { sourceResultId } : {}),
					},
					onChange: (action, attributes) =>
						sendAnalyticsEventRef.current({
							eventType: 'track',
							actionSubject: 'remixResult',
							// Result kind is inferred from dynamicUiType ('infographic' | 'image' = media, otherwise MaUI app).
							// If kinds ever share a dynamicUiType, add and register an actionSubjectId.
							action,
							attributes,
						}),
				});
			}
			if (!shouldDelayActionedEvent) {
				return;
			}
			const trackingKey = `${receiverId}:${identity.attribute}:${
				identity.attribute === 'mauiVersion' ? `${identity.localId}:` : ''
			}${identity.value}`;
			if (
				!isActiveRef.current ||
				pendingKeysRef.current.has(trackingKey) ||
				completedKeysRef.current.has(trackingKey)
			) {
				return;
			}

			pendingKeysRef.current.add(trackingKey);
			const timeoutId = setTimeout(
				() => {
					timeoutIdsRef.current.delete(trackingKey);
					try {
						void getBrowserBridgeAPI()
							.send(ReadContentCommand, {
								senderId: ROVO_CHAT_CLIENT_ID,
								receiverId: getConfluenceEditorClientId(receiverId),
								...(interactionSource ? { source: interactionSource } : {}),
							})
							.result()
							.then((content) => {
								if (
									isActiveRef.current &&
									pendingKeysRef.current.has(trackingKey) &&
									content?.contentType === 'page' &&
									adfContainsIdentity(content.pageBody, identity)
								) {
									completedKeysRef.current.add(trackingKey);
									trackAIMAUEventRef.current(analyticsEvent);
									if (adoptedAnalyticsEvent) {
										trackAIMAUEventRef.current(adoptedAnalyticsEvent);
									}
								}
							})
							.catch(() => undefined)
							.finally(() => pendingKeysRef.current.delete(trackingKey));
					} catch {
						pendingKeysRef.current.delete(trackingKey);
					}
				},
				Math.max(0, ACTIONED_EVENT_DELAY - (Date.now() - insertedAt)),
			);
			timeoutIdsRef.current.set(trackingKey, timeoutId);
		},
		[],
	);
};
